/**
 * Shared utilities for React Query integration
 *
 * This module provides common utilities used across query modules.
 */
import type { AxiosError } from 'axios';

import { parseError } from 'lib/api/errors/parseError';
import type { ApiErrorType } from 'models';
import { useSnackbarStore } from 'stores/zustand/snackbarStore';
import { getErrorMessage } from 'utils/errors';

/**
 * Utility to remove undefined/null values from params
 * Used for cleaning query keys and preventing unnecessary cache misses
 */
export const removeEmpty = <T extends Record<string, unknown>>(obj?: T): Record<string, unknown> => {
    if (!obj) return {};
    return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v !== undefined && v !== null));
};

/**
 * Surface a request failure as a negative snackbar. Pairs with
 * `meta: { globalErrorToast: false }` on mutations that own their error UX.
 */
export function pushErrorSnackbar(error: unknown): void {
    useSnackbarStore.getState().actions.pushMessage({
        text: getErrorMessage(parseError(error as AxiosError<ApiErrorType>)),
        variant: 'negative',
    });
}
